import apiClient from './apiClient';
import { FEATURE_INTEGRATION_UDF } from '../constants/capabilities';

const CAPABILITY_CACHE_TTL_MS = 5000;
let capabilityCache = { data: null, fetchedAt: 0 };

const isCapabilityDisabled = (capabilities) => {
    const featureMap = capabilities?.features;
    if (!featureMap || typeof featureMap !== 'object') return false;
    return featureMap[FEATURE_INTEGRATION_UDF] === false;
};

const getCachedCapabilities = async () => {
    const now = Date.now();
    if (capabilityCache.data && now - capabilityCache.fetchedAt < CAPABILITY_CACHE_TTL_MS) {
        return capabilityCache.data;
    }
    try {
        const response = await apiClient.get('/auth/me/capabilities', { suppressGlobalErrorToast: true });
        capabilityCache = { data: response.data, fetchedAt: now };
        return response.data;
    } catch {
        return null;
    }
};

const throwCapabilityDisabledError = () => {
    const error = new Error('UDF capability is disabled for this plan.');
    error.code = 'capability_disabled';
    error.featureKey = FEATURE_INTEGRATION_UDF;
    throw error;
};

export const udfApi = {
    invalidateCapabilityCache: () => {
        capabilityCache = { data: null, fetchedAt: 0 };
    },
    /**
     * List all UDFs for the current user
     */
    listUDFs: async () => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) return [];

        try {
            const response = await apiClient.get('/udfs', {
                suppressGlobalErrorToast: true
            });
            return response.data;
        } catch (error) {
            if (error?.response?.status === 403) return [];
            throw error;
        }
    },

    /**
     * Create a new custom UDF or import from BigQuery
     */
    createUDF: async (payload) => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) throwCapabilityDisabledError();
        const response = await apiClient.post('/udfs', payload);
        return response.data;
    },

    /**
     * Get details of a specific UDF
     */
    getUDF: async (udfId) => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) throwCapabilityDisabledError();
        const response = await apiClient.get(`/udfs/${udfId}`);
        return response.data;
    },

    /**
     * Update a custom UDF
     */
    updateUDF: async (udfId, payload) => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) throwCapabilityDisabledError();
        const response = await apiClient.patch(`/udfs/${udfId}`, payload);
        return response.data;
    },

    /**
     * Delete a UDF
     */
    deleteUDF: async (udfId) => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) throwCapabilityDisabledError();
        const response = await apiClient.delete(`/udfs/${udfId}`);
        return response.data;
    },

    /**
     * Re-generate UDF description using LLM
     */
    generateDescription: async (udfId) => {
        const capabilities = await getCachedCapabilities();
        if (isCapabilityDisabled(capabilities)) throwCapabilityDisabledError();
        const response = await apiClient.post(`/udfs/${udfId}/generate-description`);
        return response.data;
    }
};
